package fr.frux.core.maps

import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.onEach

/** Issue d'une demande de position. Exhaustive : chaque cas a un rendu cote UI. */
sealed interface LocationResult {
    data class Available(
        val location: Location,
    ) : LocationResult

    /** La permission n'est pas accordee. Demander la permission est du ressort de l'UI. */
    data object PermissionMissing : LocationResult

    /** Permission accordee mais aucune position obtenue : GPS coupe, timeout, aucun fix. */
    data object Unavailable : LocationResult
}

/**
 * Lecture de la position courante. Ne demande jamais de permission et ne leve jamais :
 * l'absence de droit se traduit par [LocationResult.PermissionMissing].
 */
interface LocationProvider {
    suspend fun currentLocation(): LocationResult

    /** Permission de localisation accordee, sans jamais la demander ni lancer de fix. */
    suspend fun hasPermission(): Boolean

    /**
     * Derniere position connue, sans jamais declencher de fix frais : rendue en quelques
     * millisecondes ou pas du tout. Null si aucune position n'a jamais ete obtenue (ou pas de
     * permission). Pour les ecrans de liste ou une position approximative suffit et ou attendre
     * un fix (plusieurs secondes) bloquerait l'affichage.
     */
    suspend fun lastKnownLocation(): Location? = null

    /**
     * Positions successives de l'appareil, pour que le point bleu suive la personne. Flux froid :
     * l'abonnement a la collecte demarre le suivi de la plateforme, l'annulation l'arrete. Ne
     * demande jamais de permission et ne leve jamais : sans droit, le flux reste vide ou se
     * termine. Le declenchement (premier plan seulement) est l'affaire de l'appelant.
     */
    fun locationUpdates(): Flow<Location> = emptyFlow()
}

expect fun createLocationProvider(): LocationProvider

/**
 * Memorise en session la derniere position obtenue avec succes, quel que soit l'appelant : un
 * ecran de liste profite ainsi du fix deja paye par la carte, sans repasser par la plateforme.
 * Une seule instance via le graphe DI (singleton), le cache est donc un simple champ.
 */
internal class SessionCachedLocationProvider(
    private val delegate: LocationProvider,
) : LocationProvider {
    private var sessionLast: Location? = null

    override suspend fun currentLocation(): LocationResult =
        delegate.currentLocation().also { result ->
            when (result) {
                is LocationResult.Available -> sessionLast = result.location
                LocationResult.PermissionMissing -> sessionLast = null
                LocationResult.Unavailable -> Unit
            }
        }

    override suspend fun hasPermission(): Boolean = delegate.hasPermission()

    // Le flux alimente le meme cache qu'un fix ponctuel : une liste ouverte apres une marche
    // trie par la position courante, pas par celle du dernier tap.
    override fun locationUpdates(): Flow<Location> = delegate.locationUpdates().onEach { sessionLast = it }

    override suspend fun lastKnownLocation(): Location? {
        // Une position memorisee ne survit pas a un retrait de permission (#607) : la rendre
        // contournerait le refus, et un ecran de liste trierait encore par elle.
        if (!delegate.hasPermission()) {
            sessionLast = null
            return null
        }
        return sessionLast ?: delegate.lastKnownLocation()?.also { sessionLast = it }
    }
}
